import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Collapse, Input, Select, Typography, message } from 'antd'
import { KeyOutlined, CopyOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getServicePrincipals,
  ServicePrincipalActions,
} from '~/Store/ServicePrincipal'
import {
  ApiKeyActions,
  getCreatedApiKey,
  getIsCreatingApiKey,
  getIsLoadingProducts,
  getProductClaimsMap,
  getProducts,
} from '~/Store/ApiKey'
import { Modal } from '~/Components/Modal'
import { ProductHeader } from './ProductHeader'
import { ProductPermissions } from './ProductPermissions'
import { ExpirySelector, ExpirySelection, resolveExpiresAt } from './ExpirySelector'

const { Panel } = Collapse
const { Option } = Select

export const CreateApiKey = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<{ servicePrincipalId?: string } | undefined>()
  const dispatch = useAppDispatch()
  const servicePrincipals = useAppSelector(getServicePrincipals)
  const products = useAppSelector(getProducts)
  const productClaimsMap = useAppSelector(getProductClaimsMap)
  const isLoadingProducts = useAppSelector(getIsLoadingProducts)
  const isCreating = useAppSelector(getIsCreatingApiKey)
  const createdApiKey = useAppSelector(getCreatedApiKey)

  const [servicePrincipalId, setServicePrincipalId] = useState<
    string | undefined
  >(location.state?.servicePrincipalId)
  const [name, setName] = useState('')
  const [expirySelection, setExpirySelection] = useState<ExpirySelection>({
    kind: 'preset',
    days: 90,
  })
  const [expandedProductIds, setExpandedProductIds] = useState<string[]>([])
  const [selectedClaimIds, setSelectedClaimIds] = useState<
    Record<string, string[]>
  >({})

  useEffect(() => {
    dispatch(ServicePrincipalActions.getServicePrincipals({ paging: false }))
    dispatch(ApiKeyActions.getProducts())
  }, [])

  useEffect(() => {
    if (!servicePrincipalId) return
    products.forEach(product => {
      dispatch(
        ApiKeyActions.getProductClaims({
          servicePrincipalId,
          productId: product.guid,
          audience: product.clientId,
        }),
      )
    })
  }, [servicePrincipalId, products])

  // Claim selections are scoped to the previously-selected Service Principal —
  // clear them when switching so a different SP's permissions can't leak in.
  useEffect(() => {
    setSelectedClaimIds({})
    setExpandedProductIds([])
  }, [servicePrincipalId])

  // Products the Service Principal has no permissions for sink to the bottom
  // (stable sort keeps the original ordering within each group).
  const sortedProducts = useMemo(() => {
    if (!servicePrincipalId) return products
    const hasClaims = (productId: string) =>
      (productClaimsMap[`${servicePrincipalId}:${productId}`]?.length ?? 0) > 0
    return [...products].sort((a, b) => {
      const aHas = hasClaims(a.guid)
      const bHas = hasClaims(b.guid)
      if (aHas === bHas) return 0
      return aHas ? -1 : 1
    })
  }, [products, productClaimsMap, servicePrincipalId])

  const totalServicesSelected = Object.values(selectedClaimIds).filter(
    ids => ids.length > 0,
  ).length
  const totalPermissionsSelected = Object.values(selectedClaimIds).reduce(
    (sum, ids) => sum + ids.length,
    0,
  )

  const toggleProduct = (
    productId: string,
    checked: boolean,
    allClaimIds: string[],
  ) => {
    setSelectedClaimIds(prev => ({
      ...prev,
      [productId]: checked ? allClaimIds : [],
    }))
    if (checked && !expandedProductIds.includes(productId)) {
      setExpandedProductIds(prev => [...prev, productId])
    }
  }

  const toggleClaim = (
    productId: string,
    claimId: string,
    checked: boolean,
  ) => {
    setSelectedClaimIds(prev => {
      const current = prev[productId] || []
      return {
        ...prev,
        [productId]: checked
          ? [...current, claimId]
          : current.filter(id => id !== claimId),
      }
    })
  }

  const deselectAll = (productId: string) => {
    setSelectedClaimIds(prev => ({ ...prev, [productId]: [] }))
  }

  const cancelClick = () => {
    history.goBack()
  }

  const generateClick = () => {
    if (!servicePrincipalId || !name) return
    const expiresAt = resolveExpiresAt(expirySelection)
    const productClaimIds = Object.values(selectedClaimIds).flat()
    dispatch(
      ApiKeyActions.createApiKey({
        servicePrincipalId,
        data: { name, expiresAt },
        productClaimIds,
      }),
    )
  }

  const closeResultModal = () => {
    dispatch(ApiKeyActions.resetCreatedApiKey())
    history.goBack()
  }

  const copyRawKey = () => {
    if (createdApiKey?.rawKey) {
      navigator.clipboard.writeText(createdApiKey.rawKey)
      message.success(t('admin.akCopied'))
    }
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <KeyOutlined style={{ marginRight: '8px' }} />
            {t('admin.akNewApiKey')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('admin.akDescription')}
          </Typography.Text>
        </div>
        <div>
          <Button onClick={cancelClick} style={{ marginRight: '10px' }}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            loading={isCreating}
            disabled={!servicePrincipalId || !name}
            onClick={generateClick}
          >
            {t('admin.akGenerateKey')}
          </Button>
        </div>
      </div>

      <Card title={t('admin.akKeyDetails')} style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '6px' }}>
            {t('admin.akServicePrincipal')} <span style={{ color: 'red' }}>*</span>
          </div>
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
            placeholder={t('admin.akSelectServicePrincipal')}
            value={servicePrincipalId}
            onChange={setServicePrincipalId}
          >
            {servicePrincipals.map(sp => (
              <Option key={sp.id} value={sp.id}>
                {sp.name}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '6px' }}>
            {t('admin.name')} <span style={{ color: 'red' }}>*</span>
          </div>
          <Input
            placeholder={t('admin.akNamePlaceholder')}
            value={name}
            onChange={evt => setName(evt.target.value)}
          />
        </div>

        <div>
          <div style={{ marginBottom: '6px' }}>{t('admin.akExpiry')}</div>
          <ExpirySelector value={expirySelection} onChange={setExpirySelection} />
        </div>
      </Card>

      <Card
        title={t('admin.akServiceProviders')}
        extra={
          <span>
            {t('admin.akSelectedCount', {
              count: totalServicesSelected,
            })}
          </span>
        }
        loading={isLoadingProducts}
      >
        {!servicePrincipalId ? (
          <Typography.Text type="secondary">
            {t('admin.akSelectSpFirst')}
          </Typography.Text>
        ) : (
          <Collapse
            activeKey={expandedProductIds}
            onChange={keys => setExpandedProductIds(keys as string[])}
          >
            {sortedProducts.map(product => (
              <Panel
                key={product.guid}
                header={
                  <ProductHeader
                    product={product}
                    servicePrincipalId={servicePrincipalId}
                    selectedClaimIds={selectedClaimIds[product.guid] || []}
                    onToggleProduct={(checked, allClaimIds) =>
                      toggleProduct(product.guid, checked, allClaimIds)
                    }
                  />
                }
              >
                <ProductPermissions
                  productId={product.guid}
                  servicePrincipalId={servicePrincipalId}
                  selectedClaimIds={selectedClaimIds[product.guid] || []}
                  onToggleClaim={(claimId, checked) =>
                    toggleClaim(product.guid, claimId, checked)
                  }
                  onDeselectAll={() => deselectAll(product.guid)}
                />
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      <Modal
        visible={!!createdApiKey}
        title={t('admin.akKeyCreated')}
        onCancel={closeResultModal}
        footer={
          <Button type="primary" onClick={closeResultModal}>
            {t('admin.akDone')}
          </Button>
        }
      >
        <Typography.Paragraph type="warning">
          {t('admin.akShowOnceWarning')}
        </Typography.Paragraph>
        <Input.Group compact style={{ display: 'flex' }}>
          <Input readOnly value={createdApiKey?.rawKey} style={{ flex: 1 }} />
          <Button icon={<CopyOutlined />} onClick={copyRawKey} />
        </Input.Group>
      </Modal>
    </div>
  )
}
